import { useEffect, useRef, useState } from 'react';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdDelete,
  MdSearch,
  MdSwapVert,
} from 'react-icons/md';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Warehouse } from '@views/WarehouseDetails/WarehouseDetails';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalHeader,
} from '@components/Modal';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { EmailVerificationBanner } from '@components/EmailVerificationBanner';
import { EngageLspModal } from '@components/EngageLspModal';
import { FilterSelect } from '@tailwindComponents/FilterSelect';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import {
  DELETE_WAREHOUSE_MATCH,
  GET_MATCH_SIGNING_DETAILS,
  MATCH_LIST,
  SUPPORTED_CURRENCY_GROUPS,
  WAREHOUSE_IMAGES_URL,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import { StockAdjustmentForm } from './StockAdjustmentForm';

const ROW_ACTION_LABELS: Record<string, string> = {
  'Not engaged': 'Rate negotiations',
  Negotiating: 'Review rates',
  'Reviewing T&Cs': 'Sign contract',
};

type WarehouseMatch = Warehouse & {
  matchStatus: string;
  matchId: string;
  isActionRequiredByShipper: boolean;
  hasInitialStockAdjustmentBeenMade: boolean;
  isSignedByShipper: boolean;
  isSignedByLsp: boolean;
};

export function WarehouseShortlist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFromHolly = searchParams.get('source') === 'holly';
  const [warehouses, setWarehouses] = useState<WarehouseMatch[]>([]);
  const [isWarehousesLoading, setIsWarehousesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideDeleted, setHideDeleted] = useState(false);
  const [hideRejected, setHideRejected] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [priceSort, setPriceSort] = useState<'none' | 'asc' | 'desc'>('none');
  const [isAwaitingHollyShortlist, setIsAwaitingHollyShortlist] =
    useState(isFromHolly);
  const hollyRetryCount = useRef(0);
  const hollyRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 10 × 3s = 30s polling window. The async shortlist-build handler on the
  // backend (ShortlistWarehousesForRegisteredUserRequested) can take several
  // seconds to create WarehouseMatch records after registration completes,
  // and the previous 5-retry budget was too tight — the page would land
  // empty before the matches existed.
  const HOLLY_MAX_RETRIES = 10;
  const HOLLY_RETRY_INTERVAL_MS = 3000;
  const [stockAdjustmentWarehouseMatchId, setStockAdjustmentWarehouseMatchId] =
    useState('');
  const [stockAdjustmentWarehouseName, setStockAdjustmentWarehouseName] =
    useState('');
  const [
    stockAdjustmentWarehouseHasAdjustedStock,
    setStockAdjustmentWarehouseHasAdjustedStock,
  ] = useState(false);
  const [isStockAdjustmentModalOpen, setIsStockAdjustmentModalOpen] =
    useState(false);
  const [engageLspWarehouseId, setEngageLspWarehouseId] = useState('');
  const [engageLspWarehouseName, setEngageLspWarehouseName] = useState('');
  const [isEngageLspModalOpen, setIsEngageLspModalOpen] = useState(false);
  // This state is used to store signing details fetched asynchronously
  // TODO: Display signing details in the UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [matchesSigningDetails, setMatchesSigningDetails] = useState<
    {
      isSigned: boolean;
      signedAt: string;
      signedByEmail: string;
      signingUrl: string;
      matchId?: string;
    }[]
  >([]);
  const authHttp = useAuthHttp();
  const navigate = useNavigate();

  const fetchWarehouseShortlist = async () => {
    setIsWarehousesLoading(true);
    try {
      const { data: warehouseShortlist } = await authHttp.post(
        MATCH_LIST,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const getPalletStoragePricePerWeek = (match: any) => {
        const rates = match.match.serviceRates?.storagePricePerWeek;
        if (
          match.match.isActionRequiredByShipper &&
          rates?.lspProposedValue != null
        ) {
          return rates.lspProposedValue;
        }
        if (
          match.match.isActionRequiredByLsp &&
          rates?.shipperProposedValue != null
        ) {
          return rates.shipperProposedValue;
        }
        if (rates?.acceptedValue != null) {
          return rates.acceptedValue;
        }
        return match.warehouse.palletStoragePricePerWeek;
      };

      if (warehouseShortlist.matches.length > 0) {
        const newWarehousesMatches = warehouseShortlist.matches.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (match: any) => ({
            ...match.warehouse,
            matchStatus: match.match.matchStatus,
            matchId: match.match.id,
            isActionRequiredByShipper: match.match.isActionRequiredByShipper,
            hasInitialStockAdjustmentBeenMade:
              match.match.hasInitialStockAdjustmentBeenMade,
            palletStoragePricePerWeek: getPalletStoragePricePerWeek(match),
            isSignedByShipper: match.match.legalDocuments[0]?.isSignedByShipper,
            isPartialProfile: match.warehouse.isPartialProfile,
          })
        );

        setWarehouses(newWarehousesMatches);
        setIsAwaitingHollyShortlist(false);
        // Clean up Holly source param once warehouses are loaded
        if (searchParams.get('source') === 'holly') {
          searchParams.delete('source');
          setSearchParams(searchParams, { replace: true });
        }
      } else {
        setWarehouses([]);
        // Auto-retry for Holly-sourced users awaiting async shortlisting.
        // isAwaitingHollyShortlist keeps the loading spinner visible via the
        // JSX condition below; it flips false either when matches arrive
        // (above) or when the retry budget is exhausted (here).
        if (
          isAwaitingHollyShortlist &&
          hollyRetryCount.current < HOLLY_MAX_RETRIES
        ) {
          hollyRetryCount.current += 1;
          hollyRetryTimer.current = setTimeout(() => {
            fetchWarehouseShortlist();
          }, HOLLY_RETRY_INTERVAL_MS);
        } else if (isAwaitingHollyShortlist) {
          setIsAwaitingHollyShortlist(false);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsWarehousesLoading(false);
    }
  };

  const handleDeleteMatch = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    matchId: string
  ) => {
    e.stopPropagation();
    setIsWarehousesLoading(true);
    try {
      const { data: matchDeleted } = await authHttp.post(
        DELETE_WAREHOUSE_MATCH,
        {
          match: {
            matchId,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (matchDeleted.match) {
        fetchWarehouseShortlist();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsWarehousesLoading(false);
    }
  };

  const handleFetchMatchSigningDetails = async (matchId: string) => {
    try {
      const { data: signingDetails } = await authHttp.get(
        GET_MATCH_SIGNING_DETAILS,
        {
          params: {
            matchId,
          },
        }
      );

      if (signingDetails) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setMatchesSigningDetails((prev: any) => [
          ...prev,
          { matchId, ...signingDetails },
        ]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsWarehousesLoading(false);
    }
  };

  const handleAdjustStock = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    warehouseMatchId: string,
    warehouseName: string,
    hasAdjustedStock: boolean
  ) => {
    e.stopPropagation();
    setStockAdjustmentWarehouseMatchId(warehouseMatchId);
    setStockAdjustmentWarehouseName(warehouseName);
    setStockAdjustmentWarehouseHasAdjustedStock(hasAdjustedStock);
    setIsStockAdjustmentModalOpen(true);
  };

  useEffect(() => {
    fetchWarehouseShortlist();
    return () => {
      if (hollyRetryTimer.current) {
        clearTimeout(hollyRetryTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    warehouses.forEach((contract) => {
      if (contract.isSignedByShipper === false) {
        handleFetchMatchSigningDetails(contract.matchId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouses]);

  const currencies = SUPPORTED_CURRENCY_GROUPS.flatMap(
    (currencyGroup) => currencyGroup.currencies
  );

  const getCurrencySymbol = (currencyCode: string) => {
    return (
      currencies.find((currency) => currency.code === currencyCode)?.symbol ||
      '£'
    );
  };

  const getFleetBreakdown = (fleet: { type: string; count: number }[]) => {
    const breakdown: { label: string; count: number }[] = [];

    if (!Array.isArray(fleet) || fleet.length === 0) return null;

    fleet.forEach(({ type, count }) => {
      if (
        type === 'MultiAxleArticMoreThan26t' ||
        type === 'TwoAxleRigidLessThan26t' ||
        type === 'ThreeAxleRigidLessThan26t'
      ) {
        const existing = breakdown.find((b) => b.label === 'Artics > 26t');
        if (existing) {
          existing.count += count;
        } else {
          breakdown.push({ label: 'Artics > 26t', count });
        }
      } else if (type === 'LgvLessThan3AndHalft') {
        const existing = breakdown.find((b) => b.label === 'LDVs < 3.5t');
        if (existing) {
          existing.count += count;
        } else {
          breakdown.push({ label: 'LDVs < 3.5t', count });
        }
      } else if (type === 'VanLessThan1AndHalft') {
        const existing = breakdown.find((b) => b.label === 'Van < 1.5t');
        if (existing) {
          existing.count += count;
        } else {
          breakdown.push({ label: 'Van < 1.5t', count });
        }
      } else if (
        type === 'TwoAxleRigid18t' ||
        type === 'TwoAxleRigidLessThan7AndHalf5t'
      ) {
        const existing = breakdown.find((b) => b.label === 'Rigids');
        if (existing) {
          existing.count += count;
        } else {
          breakdown.push({ label: 'Rigids', count });
        }
      }
    });

    return breakdown;
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'Not engaged':
        'bg-blue-50 text-blue-700 border border-blue-200 ring-1 ring-inset ring-blue-700/10',
      Negotiating:
        'bg-yellow-50 text-yellow-800 border border-yellow-200 ring-1 ring-inset ring-yellow-600/20',
      'Reviewing T&Cs':
        'bg-purple-50 text-purple-700 border border-purple-200 ring-1 ring-inset ring-purple-700/10',
      Contracted:
        'bg-green-50 text-green-700 border border-green-200 ring-1 ring-inset ring-green-600/20',
      Quarantined:
        'bg-cyan-50 text-cyan-700 border border-cyan-200 ring-1 ring-inset ring-cyan-700/10',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
          statusColors[status] ||
          'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      >
        {status === 'Not engaged' ? 'Non-engaged' : status}
      </span>
    );
  };

  const STATUS_FILTER_ORDER = [
    'Not engaged',
    'Negotiating',
    'Reviewing T&Cs',
    'Contracted',
    'Quarantined',
    'Rejected',
    'Deleted',
  ];
  const STATUS_FILTER_LABELS: Record<string, string> = {
    'Not engaged': 'Non-engaged',
  };
  const statusOptions = STATUS_FILTER_ORDER.filter((status) =>
    warehouses.some((warehouse) => warehouse.matchStatus === status)
  ).map((status) => ({
    value: status,
    label: STATUS_FILTER_LABELS[status] ?? status,
  }));

  const isStatusFilterActive = statusFilter !== null;

  const cyclePriceSort = () =>
    setPriceSort((current) =>
      current === 'none' ? 'asc' : current === 'asc' ? 'desc' : 'none'
    );

  const clearViewFilters = () => {
    setSearchQuery('');
    setStatusFilter(null);
    setHideDeleted(false);
    setHideRejected(false);
  };

  const priceSortLabel =
    priceSort === 'asc'
      ? 'Price: low to high'
      : priceSort === 'desc'
      ? 'Price: high to low'
      : 'Sort by price';

  const priceSortIcon =
    priceSort === 'asc' ? (
      <MdArrowUpward className='w-4 h-4' />
    ) : priceSort === 'desc' ? (
      <MdArrowDownward className='w-4 h-4' />
    ) : (
      <MdSwapVert className='w-4 h-4' />
    );

  const filterToggleClasses = (active: boolean, disabled = false) =>
    `flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 ${
      disabled
        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
        : active
        ? 'border-primary bg-blue-50 text-primary'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
    }`;

  const priceOf = (warehouse: WarehouseMatch) => {
    const value = Number(warehouse.palletStoragePricePerWeek);
    return Number.isFinite(value) ? value : null;
  };

  const visibleWarehouses = warehouses.filter((warehouse) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      (warehouse.name || '').toLowerCase().includes(query) ||
      (warehouse.address || '').toLowerCase().includes(query);
    if (!matchesSearch) return false;

    if (isStatusFilterActive) return warehouse.matchStatus === statusFilter;

    if (hideDeleted && warehouse.matchStatus === 'Deleted') return false;
    if (hideRejected && warehouse.matchStatus === 'Rejected') return false;
    return true;
  });

  const filteredWarehouses =
    priceSort === 'none'
      ? visibleWarehouses
      : [...visibleWarehouses].sort((a, b) => {
          const priceA = priceOf(a);
          const priceB = priceOf(b);
          if (priceA === null && priceB === null) return 0;
          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceSort === 'asc' ? priceA - priceB : priceB - priceA;
        });

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Warehouse', href: '/warehouse-search', current: false },
    {
      name: 'Shortlisted & contracted LSPs',
      href: '/warehouse-shortlist',
      current: true,
    },
  ];

  return (
    <>
      <PageHeader header='Warehouses shortlist' pages={pages}>
        <Button
          label='New Search'
          intent='primary'
          startIcon={<HiOutlineOfficeBuilding />}
          onClick={() => navigate('/warehouse-search')}
        />
      </PageHeader>
      <main className='flex flex-col gap-4 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8 relative'>
        <EmailVerificationBanner />
        <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
          {/* Header with search */}
          <div className='flex flex-wrap items-center gap-3 p-4 border-b border-gray-200'>
            {/* Checkbox for select all */}
            <input
              type='checkbox'
              className='w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer'
            />
            {/* Search input */}
            <div className='relative flex-1 min-w-[220px] max-w-sm'>
              <input
                type='text'
                placeholder='Name, address or keyword...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              />
              <MdSearch className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
            </div>

            {/* View filters */}
            <div className='flex flex-wrap items-center gap-2 lg:ml-auto'>
              <div className='w-40'>
                <FilterSelect
                  placeholder='All statuses'
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>

              <button
                type='button'
                aria-pressed={priceSort !== 'none'}
                onClick={cyclePriceSort}
                className={filterToggleClasses(priceSort !== 'none')}
              >
                {priceSortIcon}
                {priceSortLabel}
              </button>

              <button
                type='button'
                aria-pressed={hideDeleted}
                disabled={isStatusFilterActive}
                onClick={() => setHideDeleted((current) => !current)}
                className={filterToggleClasses(hideDeleted, isStatusFilterActive)}
              >
                {hideDeleted ? 'Show deleted' : 'Hide deleted'}
              </button>

              <button
                type='button'
                aria-pressed={hideRejected}
                disabled={isStatusFilterActive}
                onClick={() => setHideRejected((current) => !current)}
                className={filterToggleClasses(hideRejected, isStatusFilterActive)}
              >
                {hideRejected ? 'Show rejected' : 'Hide rejected'}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-gray-200 bg-white'>
                  <th className='w-12 px-4 py-3'>
                    <span className='sr-only'>Select</span>
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px]'>
                    {/* Image + Name */}
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Capacity
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Availability
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Rates
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Own fleet
                  </th>
                  <th className='px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='w-12 px-4 py-3'>
                    <span className='sr-only'>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {(isWarehousesLoading || isAwaitingHollyShortlist) && (
                  <tr>
                    <td colSpan={8} className='text-center py-12'>
                      <div className='flex flex-col items-center gap-3'>
                        <FloxSpinner size='md' />
                        <p className='text-gray-500 text-sm'>
                          {isAwaitingHollyShortlist
                            ? 'Setting up your shortlist based on your conversation...'
                            : 'Loading warehouses...'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {!isWarehousesLoading &&
                  !isAwaitingHollyShortlist &&
                  filteredWarehouses.length === 0 &&
                  (warehouses.length > 0 ? (
                    <tr>
                      <td colSpan={8} className='text-center py-12'>
                        <div className='flex flex-col items-center gap-3'>
                          <HiOutlineOfficeBuilding className='w-12 h-12 text-gray-300' />
                          <p className='text-gray-900 font-medium'>
                            No matches for the current filters
                          </p>
                          <button
                            type='button'
                            onClick={clearViewFilters}
                            className='text-sm text-blue-600 hover:text-blue-800 underline'
                          >
                            Clear filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={8} className='text-center py-12'>
                        <div className='flex flex-col items-center gap-3'>
                          <HiOutlineOfficeBuilding className='w-12 h-12 text-gray-300' />
                          <p className='text-gray-900 font-medium'>
                            No warehouses found
                          </p>
                          <p className='text-gray-500 text-sm'>
                            Find a warehouse using our{' '}
                            <a
                              href='/warehouse-search'
                              className='text-blue-600 hover:text-blue-800 underline'
                            >
                              search
                            </a>{' '}
                            tool
                          </p>
                        </div>
                      </td>
                    </tr>
                  ))}

                {filteredWarehouses.map((warehouse) => {
                  const currencySymbol = getCurrencySymbol(
                    warehouse.currency || 'GBP'
                  );
                  const fleetBreakdown = getFleetBreakdown(warehouse.fleet);

                  return (
                    <tr key={warehouse.matchId} className='hover:bg-gray-50'>
                      {/* Checkbox */}
                      <td className='px-4 py-4'>
                        <input
                          type='checkbox'
                          className='w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer'
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>

                      {/* Image + Name + Address */}
                      <td className='px-4 py-4'>
                        <div className='flex items-center gap-4'>
                          <div className='w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0'>
                            {warehouse.images && warehouse.images.length > 0 ? (
                              <img
                                src={`${WAREHOUSE_IMAGES_URL}/${warehouse.images[0].imageLocation}/small/${warehouse.images[0].remoteName}`}
                                alt={warehouse.name}
                                className='w-full h-full object-cover'
                              />
                            ) : (
                              <div className='w-full h-full flex items-center justify-center'>
                                <HiOutlineOfficeBuilding className='w-8 h-8 text-gray-400' />
                              </div>
                            )}
                          </div>
                          <div className='flex flex-col items-start gap-1'>
                            <span className='text-sm font-medium text-gray-900'>
                              {warehouse.name}
                            </span>
                            <span className='text-sm text-gray-500'>
                              {warehouse.address}
                            </span>
                            <Button
                              label='Open site profile'
                              intent='secondary'
                              size='xs'
                              className='h-auto whitespace-nowrap'
                              onClick={() =>
                                navigate(`/warehouse/${warehouse.warehouseId}`)
                              }
                            />
                          </div>
                        </div>
                      </td>

                      {/* Capacity */}
                      <td className='px-4 py-4'>
                        <div className='flex flex-col gap-1'>
                          <div>
                            <span className='text-sm font-medium text-gray-900'>
                              {warehouse.maxStorageCapacity}{' '}
                              {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                            </span>
                            <p className='text-xs text-gray-500'>
                              Max storage capacity
                            </p>
                          </div>
                          {warehouse.minStorageCapacityThreshold && (
                            <div>
                              <span className='text-sm font-medium text-gray-900'>
                                {warehouse.minStorageCapacityThreshold}{' '}
                                {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                              </span>
                              <p className='text-xs text-gray-500'>
                                Min storage capacity threshold
                              </p>
                            </div>
                          )}
                          {warehouse.minStorageTimeThreshold && (
                            <div>
                              <span className='text-sm font-medium text-gray-900'>
                                {warehouse.minStorageTimeThreshold}{' '}
                                {warehouse.storageTimeUnitOfMeasure?.toLowerCase()}
                              </span>
                              <p className='text-xs text-gray-500'>
                                Min storage time threshold
                              </p>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Availability */}
                      <td className='px-4 py-4'>
                        <div className='flex flex-col gap-1'>
                          <div>
                            <span className='text-sm font-medium text-gray-900'>
                              {warehouse.currentlyAvailableCapacity}{' '}
                              {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                            </span>
                            <p className='text-xs text-gray-500'>
                              Max storage capacity available
                            </p>
                          </div>
                          {warehouse.maxStorageCapacityAvailable !==
                            undefined && (
                            <div>
                              <span className='text-sm font-medium text-gray-900'>
                                {warehouse.maxStorageCapacityAvailable}{' '}
                                {warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}
                              </span>
                              <p className='text-xs text-gray-500'>
                                Max storage capacity available
                              </p>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Rates */}
                      <td className='px-4 py-4'>
                        <div className='flex flex-col gap-1'>
                          <div>
                            <span className='text-sm font-medium text-gray-900'>
                              {currencySymbol}
                              {warehouse.palletStoragePricePerWeek}
                            </span>
                            <p className='text-xs text-gray-500'>
                              Pallet storage (per pallet-week)
                            </p>
                          </div>
                          {warehouse.palletPickingAndDespatchPrice && (
                            <div>
                              <span className='text-sm font-medium text-gray-900'>
                                {currencySymbol}
                                {warehouse.palletPickingAndDespatchPrice}
                              </span>
                              <p className='text-xs text-gray-500'>
                                Pallet receipt and putaway
                              </p>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Own fleet */}
                      <td className='px-4 py-4'>
                        {fleetBreakdown && fleetBreakdown.length > 0 ? (
                          <div className='flex flex-col gap-1'>
                            {fleetBreakdown.map((item) => (
                              <div key={item.label}>
                                <span className='text-sm font-medium text-gray-900'>
                                  {item.count}
                                </span>
                                <p className='text-xs text-gray-500'>
                                  {item.label}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className='text-sm text-gray-500'>-</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className='px-4 py-4 text-center'>
                        {getStatusBadge(warehouse.matchStatus)}
                      </td>

                      {/* Actions */}
                      <td className='px-4 py-4'>
                        <div className='flex items-center justify-end gap-2'>
                          {warehouse.isPartialProfile &&
                            warehouse.matchStatus !== 'Contracted' && (
                              <Button
                                label='Engage LSP'
                                intent='secondary'
                                size='xs'
                                className='h-auto whitespace-nowrap'
                                onClick={() => {
                                  setEngageLspWarehouseId(
                                    warehouse.warehouseId
                                  );
                                  setEngageLspWarehouseName(warehouse.name);
                                  setIsEngageLspModalOpen(true);
                                }}
                              />
                            )}
                          {!warehouse.isPartialProfile &&
                            (warehouse.matchStatus === 'Not engaged' ||
                              warehouse.matchStatus === 'Negotiating' ||
                              warehouse.matchStatus === 'Reviewing T&Cs') && (
                              <Button
                                label={
                                  ROW_ACTION_LABELS[warehouse.matchStatus] ??
                                  'View'
                                }
                                intent='secondary'
                                size='xs'
                                className='h-auto whitespace-nowrap'
                                onClick={() => {
                                  const path = `/warehouse/${warehouse.warehouseId}/service-rates`;
                                  navigate(
                                    warehouse.matchId
                                      ? `${path}?matchId=${warehouse.matchId}`
                                      : path
                                  );
                                }}
                              />
                            )}
                          {warehouse.matchStatus === 'Contracted' && (
                            <Button
                              label='Adjust stock'
                              intent='secondary'
                              size='xs'
                              className='h-auto whitespace-nowrap'
                              onClick={(e) =>
                                handleAdjustStock(
                                  e,
                                  warehouse.matchId,
                                  warehouse.name,
                                  warehouse.hasInitialStockAdjustmentBeenMade
                                )
                              }
                            />
                          )}
                          {(warehouse.matchStatus === 'Not engaged' ||
                            warehouse.matchStatus === 'Negotiating' ||
                            warehouse.matchStatus === 'Quarantined') && (
                            <button
                              type='button'
                              onClick={(e) =>
                                handleDeleteMatch(e, warehouse.matchId)
                              }
                              className='p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors'
                            >
                              <MdDelete className='w-5 h-5' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <EngageLspModal
          open={isEngageLspModalOpen}
          onOpenChange={setIsEngageLspModalOpen}
          warehouseId={engageLspWarehouseId}
          warehouseName={engageLspWarehouseName}
        />

        <Modal
          open={isStockAdjustmentModalOpen}
          onOpenChange={(isOpen: boolean) => {
            setIsStockAdjustmentModalOpen(isOpen);
          }}
        >
          <ModalContainer preventCloseOnOutsideClick>
            <ModalHeader
              title={`One Time Stock Adjustment - ${stockAdjustmentWarehouseName}`}
            />
            <ModalContent
              css={{
                maxHeight: 'calc(85vh - 12.5rem)',
                minHeight: 'calc(85vh - 12.5rem)',
                overflow: 'hidden',
              }}
            >
              <StockAdjustmentForm
                warehouseMatchId={stockAdjustmentWarehouseMatchId}
                warehouseName={stockAdjustmentWarehouseName}
                hasAdjustedStock={stockAdjustmentWarehouseHasAdjustedStock}
                onStockAdjusted={fetchWarehouseShortlist}
              />
            </ModalContent>
          </ModalContainer>
        </Modal>
      </main>
    </>
  );
}
